// Admin → Onboarded Doctors
//
// Directory of external doctors registered through the public-facing
// website intake (or added by an admin). These are NOT system users —
// they don't log in; they're just contact records we can attach to
// referrals and other workflows.
//
// Facility assignment is optional: a doctor without a facility shows up
// as "Collection Point" on the list. Per the product owner, leaving the
// facility blank is the same as parking the doctor at the default
// collection point.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Stack, Typography, TextField, InputAdornment, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, CircularProgress, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  Tooltip, useTheme,
} from '@mui/material';
import {
  MedicalServices as DoctorIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Workspaces as SpecIcon,
  Apartment as FacilityIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
  Document      as DocxDocument,
  Packer,
  Paragraph     as DocxParagraph,
  TextRun,
  Table         as DocxTable,
  TableRow      as DocxTableRow,
  TableCell     as DocxTableCell,
  HeadingLevel,
  AlignmentType,
  WidthType,
} from 'docx';

import {
  facilitiesListUrl,
  onboardedDoctorsListUrl, onboardedDoctorCreateUrl,
  onboardedDoctorUpdateUrl, onboardedDoctorDeleteUrl,
} from '../BaseURL';

// ── Palette factory — tracks the active theme mode so the page respects
// the user's light/dark preference instead of forcing one.
const paletteFor = (isDark) => ({
  isDark,
  pageBg:       isDark ? '#0B0D14' : '#F4F6FA',
  surface:      isDark ? '#11141E' : '#FFFFFF',
  surfaceAlt:   isDark ? '#161A26' : '#F8FAFC',
  fieldBg:      isDark ? '#0F121B' : '#FFFFFF',
  borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
  borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
  text:         isDark ? '#F5F7FB' : '#0F172A',
  textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.60)',
  textDim:      isDark ? 'rgba(255,255,255,0.40)' : 'rgba(15,23,42,0.40)',
  rowHover:     isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)',
  primary:      '#3B82F6',
  primaryStrong:'#2563EB',
  emerald:      '#22c55e',
  amber:        '#f59e0b',
  danger:       '#ef4444',
  violet:       '#8b5cf6',
  violetSoft:   isDark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.14)',
  muted:        isDark ? '#94a3b8' : '#64748b',
});

const DOCTOR_TYPES = [
  { value: 'External',  label: 'External' },
  { value: 'Internal',  label: 'Internal' },
  { value: 'Visiting',  label: 'Visiting' },
  { value: 'Resident',  label: 'Resident' },
  { value: 'Consultant',label: 'Consultant' },
];

const STATUS_FILTERS = [
  { value: 'all',      label: 'Onboarded (all)' },
  { value: 'active',   label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
];

const emptyForm = {
  id: null,
  name: '',
  email: '',
  phone: '',
  specialization: '',
  doctor_type: 'External',
  facility_id: '',           // '' = no facility = Collection Point
  experience_years: '',
  is_active: true,
};

export default function OnboardedDoctors() {
  const muiTheme = useTheme();
  const T = useMemo(
    () => paletteFor(muiTheme.palette.mode === 'dark'),
    [muiTheme.palette.mode]
  );
  const sx = useMemo(() => buildSx(T), [T]);

  const [rows, setRows]             = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [me, setMe]       = useState(null);

  // ── Report dialog ───────────────────────────────────────────────────
  // Prints/exports the currently filtered list (same rows the user sees
  // on screen) so the report always matches the active search + status
  // filter — no separate query, no surprises.
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setMe(JSON.parse(raw));
    } catch (_) {}
  }, []);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await axios.get(onboardedDoctorsListUrl, { params, timeout: 12000 });
      setRows(data?.success ? (data.results || []) : []);
    } catch (err) {
      console.error('[onboardedDoctors] list failed:', err.message);
      setRows([]);
      setSnack({ open: true, severity: 'error', message: 'Could not load doctors.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const { data } = await axios.get(facilitiesListUrl, {
        params: { activeOnly: 1 }, timeout: 12000,
      });
      setFacilities(data?.success ? (data.results || []) : []);
    } catch (_) {
      setFacilities([]);
    }
  };

  useEffect(() => { fetchDoctors(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { fetchFacilities(); }, []);

  // ── Derived list (client-side filter on top of server list) ──────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      String(r.name || '').toLowerCase().includes(q) ||
      String(r.email || '').toLowerCase().includes(q) ||
      String(r.phone || '').toLowerCase().includes(q) ||
      String(r.specialization || '').toLowerCase().includes(q) ||
      String(r.facility_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  // ── Dialog ───────────────────────────────────────────────────────────
  const openCreate = () => { setForm(emptyForm); setDialogOpen(true); };
  const openEdit   = (row) => {
    setForm({
      id: row.id,
      name: row.name || '',
      email: row.email || '',
      phone: row.phone || '',
      specialization: row.specialization || '',
      doctor_type: row.doctor_type || 'External',
      facility_id: row.facility_id || '',
      experience_years: row.experience_years || '',
      is_active: !!row.is_active,
    });
    setDialogOpen(true);
  };
  const closeDialog = () => { if (!saving) setDialogOpen(false); };

  const saveDoctor = async () => {
    if (!form.name.trim()) {
      setSnack({ open: true, severity: 'error', message: 'Doctor name is required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        specialization: form.specialization.trim() || null,
        doctor_type: form.doctor_type,
        facility_id: form.facility_id || null,
        experience_years: form.experience_years === '' ? null : Number(form.experience_years),
        currentUser: me?.username || me?.name || null,
        is_active: form.is_active,
      };
      if (form.id) {
        await axios.put(onboardedDoctorUpdateUrl(form.id), payload, { timeout: 12000 });
      } else {
        await axios.post(onboardedDoctorCreateUrl, payload, { timeout: 12000 });
      }
      setDialogOpen(false);
      setSnack({
        open: true, severity: 'success',
        message: form.id ? 'Profile updated.' : 'Doctor added.',
      });
      fetchDoctors();
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not save doctor.';
      setSnack({ open: true, severity: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const deleteDoctor = async (row) => {
    const r = await Swal.fire({
      title: `Delete ${row.name}?`,
      text: 'This permanently removes the doctor profile. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: T.danger,
      cancelButtonColor: T.borderStrong,
      background: T.surface,
      color: T.text,
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(onboardedDoctorDeleteUrl(row.id), { timeout: 12000 });
      setSnack({ open: true, severity: 'success', message: 'Doctor deleted.' });
      fetchDoctors();
    } catch (err) {
      const msg = err.response?.data?.message || 'Delete failed.';
      setSnack({ open: true, severity: 'error', message: msg });
    }
  };

  // ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100%', bgcolor: T.pageBg, p: { xs: 1.5, md: 2.5 }, color: T.text }}>
      {/* ── Page header ──────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 1.75, md: 2.5 },
        mb: 2,
        bgcolor: T.surface,
        border: `1px solid ${T.borderSubtle}`,
        borderRadius: 2,
        display: 'flex', alignItems: 'center', gap: 1.75,
      }}>
        <Avatar sx={{
          bgcolor: 'rgba(59,130,246,0.16)',
          color: T.primary,
          width: 44, height: 44,
          border: `1px solid rgba(59,130,246,0.32)`,
        }}>
          <DoctorIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, color: T.text }}>
            Onboarded doctors
          </Typography>
          <Typography sx={{ color: T.textMuted, fontSize: '0.82rem', mt: 0.25 }}>
            Doctors onboarded via website requests
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            startIcon={<ReportIcon />}
            onClick={() => setReportOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: T.text,
              bgcolor: T.surfaceAlt,
              border: `1px solid ${T.borderStrong}`,
              px: 2, py: 1, borderRadius: 1.5,
              '&:hover': { bgcolor: T.rowHover, borderColor: T.primary, color: T.primary },
            }}
          >
            Report
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: '#fff',
              background: `linear-gradient(180deg, ${T.primary}, ${T.primaryStrong})`,
              px: 2, py: 1, borderRadius: 1.5,
              boxShadow: '0 8px 24px rgba(59,130,246,0.25)',
              '&:hover': { background: T.primaryStrong },
            }}
          >
            New Profile
          </Button>
        </Stack>
      </Paper>

      {/* ── Filters strip ────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 1.5, md: 2 },
        mb: 2,
        bgcolor: T.surface,
        border: `1px solid ${T.borderSubtle}`,
        borderRadius: 2,
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search doctors..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: T.textDim, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={sx.field}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={sx.select}
              MenuProps={{ PaperProps: { sx: sx.menuPaper } }}
            >
              {STATUS_FILTERS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* ── List ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.borderSubtle}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}>
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, borderBottom: `1px solid ${T.borderSubtle}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: T.text }}>
            Onboarded doctors
          </Typography>
          <Typography sx={{ color: T.textMuted, fontSize: '0.78rem', mt: 0.25 }}>
            {filtered.length} {filtered.length === 1 ? 'profile' : 'profiles'} found
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: T.primary }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <DoctorIcon sx={{ fontSize: 44, color: T.textDim, mb: 1 }} />
            <Typography sx={{ color: T.textMuted, fontWeight: 600 }}>
              {search || statusFilter !== 'all'
                ? 'No doctors match those filters.'
                : 'No onboarded doctors yet — click "New Profile" to add one.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Doctor', 'Type', 'Phone', 'Specialization', 'Facility', 'Experience', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={h + i} sx={sx.headerCell}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(row => (
                  <TableRow key={row.id} sx={{
                    '&:hover': { bgcolor: T.rowHover },
                    borderBottom: `1px solid ${T.borderSubtle}`,
                  }}>
                    <TableCell sx={sx.cell}>
                      <Typography sx={{ fontWeight: 700, color: T.text }}>
                        Dr. {row.name}
                      </Typography>
                      {row.email && (
                        <Typography sx={{ color: T.textDim, fontSize: '0.76rem', mt: 0.25 }}>
                          {row.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Chip
                        size="small"
                        label={row.doctor_type || 'External'}
                        sx={{
                          bgcolor: T.violetSoft,
                          color: T.violet,
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      {row.phone ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.primary, fontSize: '0.84rem' }}>
                          <PhoneIcon sx={{ fontSize: 13 }} />
                          <span>{row.phone}</span>
                        </Stack>
                      ) : (
                        <Typography sx={{ color: T.textDim }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      {row.specialization ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.textMuted, fontSize: '0.84rem' }}>
                          <SpecIcon sx={{ fontSize: 14 }} />
                          <span>{row.specialization}</span>
                        </Stack>
                      ) : (
                        <Typography sx={{ color: T.textDim }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      {row.facility_name ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.textMuted, fontSize: '0.82rem' }}>
                          <FacilityIcon sx={{ fontSize: 13 }} />
                          <span>{row.facility_name}</span>
                        </Stack>
                      ) : (
                        <Chip
                          size="small"
                          label="Collection Point"
                          sx={{
                            bgcolor: T.isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.06)',
                            color: T.muted,
                            fontWeight: 700, fontSize: '0.68rem', height: 22,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Typography sx={{ color: T.textMuted }}>
                        {row.experience_years ? `${row.experience_years} yr` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={sx.cell}>
                      <Chip
                        size="small"
                        label={row.is_active ? 'Active' : 'Inactive'}
                        sx={{
                          bgcolor: row.is_active ? 'rgba(34,197,94,0.14)' : (T.isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.06)'),
                          color: row.is_active ? T.emerald : T.muted,
                          fontWeight: 700, fontSize: '0.68rem', height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...sx.cell, whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}
                          sx={{ color: T.textMuted, '&:hover': { color: T.primary } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteDoctor(row)}
                          sx={{ color: T.danger, '&:hover': { bgcolor: 'rgba(239,68,68,0.10)' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Create / Edit dialog ─────────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: T.surface, color: T.text, borderRadius: 2, border: `1px solid ${T.borderSubtle}` } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${T.borderSubtle}`, fontWeight: 800 }}>
          {form.id ? 'Edit doctor profile' : 'Onboard new doctor'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
          <Stack spacing={1.75} sx={{ mt: 1 }}>
            <TextField
              label="Doctor name *"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={sx.dialogField}
              autoFocus
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Email"
                size="small"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
              <TextField
                label="Phone"
                size="small"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Specialization"
                size="small"
                value={form.specialization}
                onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
              <FormControl size="small" sx={{ ...sx.dialogField, flex: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.doctor_type}
                  onChange={(e) => setForm({ ...form, doctor_type: e.target.value })}
                  MenuProps={{ PaperProps: { sx: sx.menuPaper } }}
                >
                  {DOCTOR_TYPES.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <FormControl size="small" sx={{ ...sx.dialogField, flex: 2 }}>
                <InputLabel>Facility (optional)</InputLabel>
                <Select
                  label="Facility (optional)"
                  value={form.facility_id}
                  onChange={(e) => setForm({ ...form, facility_id: e.target.value })}
                  MenuProps={{ PaperProps: { sx: sx.menuPaper } }}
                >
                  <MenuItem value="">
                    <em>— Collection Point (none) —</em>
                  </MenuItem>
                  {facilities.map(f => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name} {f.code ? ` · ${f.code}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Experience (yrs)"
                size="small"
                type="number"
                value={form.experience_years}
                onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                sx={{ ...sx.dialogField, flex: 1 }}
              />
            </Stack>
            <Typography sx={{ color: T.textDim, fontSize: '0.74rem' }}>
              Leave the facility blank to park the doctor at the default <strong>Collection Point</strong>.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  sx={{ color: T.textMuted, '&.Mui-checked': { color: T.primary } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.85rem', color: T.text }}>Active</Typography>}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${T.borderSubtle}` }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ color: T.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={saveDoctor}
            disabled={saving}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: '#fff',
              background: `linear-gradient(180deg, ${T.primary}, ${T.primaryStrong})`,
              px: 2.5, py: 0.8, borderRadius: 1.5,
              '&:hover': { background: T.primaryStrong },
            }}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          >
            {form.id ? 'Save changes' : 'Onboard doctor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Report dialog ─────────────────────────────────────────── */}
      <OnboardedDoctorsReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        rows={filtered}
        statusFilter={statusFilter}
        search={search}
        currentUser={me?.username || me?.name || ''}
        T={T}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Report dialog — preview + Print/PDF + Word (.docx) export
//
//  The preview mirrors the on-screen list so what the admin sees here is
//  exactly what gets printed/exported. Print uses a child window with a
//  plain-HTML render so the browser's print dialog ("Save as PDF") works
//  with no extra dependencies. The .docx path uses the `docx` package
//  already in the project (frontend/package.json) — same pattern as
//  VisitRecordDialog.js.
// ─────────────────────────────────────────────────────────────────────────
function OnboardedDoctorsReportDialog({ open, onClose, rows, statusFilter, search, currentUser, T }) {
  const generatedAt = new Date();
  const statusLabel = (STATUS_FILTERS.find(s => s.value === statusFilter) || STATUS_FILTERS[0]).label;
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '—';
      return dt.toLocaleDateString();
    } catch (_) { return '—'; }
  };
  const stamp = generatedAt.toLocaleString();
  const filenameStamp = generatedAt.toISOString().slice(0, 10);

  const handlePrint = () => {
    const html = buildReportHTML({ rows, statusLabel, search, stamp, currentUser, fmtDate });
    const win = window.open('', '_blank', 'width=1000,height=800');
    if (!win) {
      alert('Pop-up blocked. Allow pop-ups for this site to print the report.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const handleDownloadDocx = async () => {
    try {
      const doc = buildReportDocx({ rows, statusLabel, search, stamp, currentUser, fmtDate });
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Onboarded_Doctors_${filenameStamp}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('docx export failed:', err);
      alert('Could not generate the Word document. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: T.surface, color: T.text, borderRadius: 2, border: `1px solid ${T.borderSubtle}` } }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${T.borderSubtle}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 1,
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReportIcon sx={{ color: T.primary }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Onboarded doctors — Report</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<DocxIcon />}
            onClick={handleDownloadDocx}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: T.text, bgcolor: T.surfaceAlt,
              border: `1px solid ${T.borderStrong}`,
              px: 1.75, py: 0.6, borderRadius: 1.5,
              '&:hover': { bgcolor: T.rowHover, color: T.primary, borderColor: T.primary },
            }}
          >
            Word (.docx)
          </Button>
          <Button
            startIcon={<PdfIcon />}
            onClick={handlePrint}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: '#fff',
              background: `linear-gradient(180deg, ${T.primary}, ${T.primaryStrong})`,
              px: 1.75, py: 0.6, borderRadius: 1.5,
              '&:hover': { background: T.primaryStrong },
            }}
          >
            Print / PDF
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, bgcolor: T.surfaceAlt }}>
        {/* Preview that matches what'll print. Plain styling so the admin
            sees the exported layout, not the MUI table. */}
        <Box sx={{
          bgcolor: '#fff', color: '#111',
          p: { xs: 2, md: 3 },
          borderRadius: 1,
          border: `1px solid ${T.borderSubtle}`,
          maxHeight: '70vh', overflowY: 'auto',
        }}>
          <Box sx={{ borderBottom: '2px solid #1f2937', pb: 1.5, mb: 2 }}>
            <Typography sx={{ fontSize: '0.72rem', letterSpacing: 1, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
              Admin · Directory
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', mt: 0.25 }}>
              Onboarded Doctors Report
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 0.75, color: '#475569', fontSize: '0.78rem' }}>
              <span><strong>Filter:</strong> {statusLabel}</span>
              {search && <span><strong>Search:</strong> "{search}"</span>}
              <span><strong>Count:</strong> {rows.length}</span>
              <span><strong>Generated:</strong> {stamp}</span>
              {currentUser && <span><strong>By:</strong> {currentUser}</span>}
            </Stack>
          </Box>

          <Box component="table" sx={{
            width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem',
            '& th, & td': { border: '1px solid #e2e8f0', padding: '6px 8px', textAlign: 'left', verticalAlign: 'top' },
            '& th': { bgcolor: '#f1f5f9', color: '#0f172a', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 },
            '& tr:nth-of-type(even) td': { bgcolor: '#f8fafc' },
          }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Doctor</th>
                <th>Type</th>
                <th>Phone</th>
                <th>Specialization</th>
                <th>Facility</th>
                <th>Exp (yrs)</th>
                <th>Status</th>
                <th>Onboarded</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#64748b', padding: '16px' }}>
                  No doctors match the current filters.
                </td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td><strong>Dr. {r.name}</strong>{r.email ? <div style={{ color: '#64748b', fontSize: '0.72rem' }}>{r.email}</div> : null}</td>
                  <td>{r.doctor_type || 'External'}</td>
                  <td>{r.phone || '—'}</td>
                  <td>{r.specialization || '—'}</td>
                  <td>{r.facility_name || 'Collection Point'}</td>
                  <td>{r.experience_years != null && r.experience_years !== '' ? r.experience_years : '—'}</td>
                  <td>{r.is_active ? 'Active' : 'Inactive'}</td>
                  <td>{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${T.borderSubtle}` }}>
        <Button onClick={onClose} sx={{ color: T.textMuted, textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Print HTML builder ───────────────────────────────────────────────────
function buildReportHTML({ rows, statusLabel, search, stamp, currentUser, fmtDate }) {
  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const bodyRows = rows.length === 0
    ? `<tr><td colspan="9" style="text-align:center;color:#64748b;padding:16px">No doctors match the current filters.</td></tr>`
    : rows.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td><strong>Dr. ${esc(r.name)}</strong>${r.email ? `<div style="color:#64748b;font-size:11px">${esc(r.email)}</div>` : ''}</td>
        <td>${esc(r.doctor_type || 'External')}</td>
        <td>${esc(r.phone || '—')}</td>
        <td>${esc(r.specialization || '—')}</td>
        <td>${esc(r.facility_name || 'Collection Point')}</td>
        <td>${esc(r.experience_years != null && r.experience_years !== '' ? r.experience_years : '—')}</td>
        <td>${r.is_active ? 'Active' : 'Inactive'}</td>
        <td>${esc(fmtDate(r.created_at))}</td>
      </tr>
    `).join('');

  return `<!doctype html><html><head><meta charset="utf-8" />
    <title>Onboarded Doctors Report</title>
    <style>
      @page { size: A4; margin: 14mm; }
      body { font-family: Arial, sans-serif; color: #111; font-size: 12px; }
      h1 { font-size: 20px; margin: 0; color: #0f172a; }
      .eyebrow { font-size: 10px; letter-spacing: 1.2px; color: #475569; font-weight: 700; text-transform: uppercase; }
      .meta { margin-top: 4px; color: #475569; font-size: 11px; }
      .meta span { margin-right: 16px; }
      .head { border-bottom: 2px solid #1f2937; padding-bottom: 8px; margin-bottom: 14px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #e2e8f0; padding: 5px 7px; text-align: left; vertical-align: top; }
      th { background: #f1f5f9; color: #0f172a; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
      tr:nth-child(even) td { background: #f8fafc; }
      .foot { margin-top: 16px; color: #94a3b8; font-size: 10px; text-align: right; }
    </style>
  </head><body>
    <div class="head">
      <div class="eyebrow">Admin · Directory</div>
      <h1>Onboarded Doctors Report</h1>
      <div class="meta">
        <span><strong>Filter:</strong> ${esc(statusLabel)}</span>
        ${search ? `<span><strong>Search:</strong> "${esc(search)}"</span>` : ''}
        <span><strong>Count:</strong> ${rows.length}</span>
        <span><strong>Generated:</strong> ${esc(stamp)}</span>
        ${currentUser ? `<span><strong>By:</strong> ${esc(currentUser)}</span>` : ''}
      </div>
    </div>
    <table>
      <thead><tr>
        <th>#</th><th>Doctor</th><th>Type</th><th>Phone</th>
        <th>Specialization</th><th>Facility</th><th>Exp (yrs)</th>
        <th>Status</th><th>Onboarded</th>
      </tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
    <div class="foot">Lab-System · Onboarded Doctors Report</div>
  </body></html>`;
}

// ── DOCX builder ─────────────────────────────────────────────────────────
function buildReportDocx({ rows, statusLabel, search, stamp, currentUser, fmtDate }) {
  const text = (s) => new DocxParagraph({ children: [new TextRun({ text: String(s ?? '') })] });
  const bold = (s, size = 22) => new DocxParagraph({
    children: [new TextRun({ text: String(s ?? ''), bold: true, size })],
  });

  const headerCell = (label) => new DocxTableCell({
    children: [new DocxParagraph({
      children: [new TextRun({ text: label, bold: true, color: '0F172A', size: 18 })],
      alignment: AlignmentType.LEFT,
    })],
    shading: { fill: 'F1F5F9' },
  });
  const bodyCell = (val) => new DocxTableCell({
    children: [new DocxParagraph({
      children: [new TextRun({ text: String(val ?? '—'), size: 18 })],
    })],
  });

  const headerRow = new DocxTableRow({
    children: [
      headerCell('#'),
      headerCell('Doctor'),
      headerCell('Type'),
      headerCell('Phone'),
      headerCell('Specialization'),
      headerCell('Facility'),
      headerCell('Exp (yrs)'),
      headerCell('Status'),
      headerCell('Onboarded'),
    ],
  });

  const dataRows = rows.length === 0
    ? [new DocxTableRow({
        children: [new DocxTableCell({
          children: [new DocxParagraph({
            children: [new TextRun({ text: 'No doctors match the current filters.', italics: true, color: '64748B' })],
          })],
          columnSpan: 9,
        })],
      })]
    : rows.map((r, i) => new DocxTableRow({
        children: [
          bodyCell(i + 1),
          bodyCell(`Dr. ${r.name || ''}`),
          bodyCell(r.doctor_type || 'External'),
          bodyCell(r.phone || '—'),
          bodyCell(r.specialization || '—'),
          bodyCell(r.facility_name || 'Collection Point'),
          bodyCell(r.experience_years != null && r.experience_years !== '' ? r.experience_years : '—'),
          bodyCell(r.is_active ? 'Active' : 'Inactive'),
          bodyCell(fmtDate(r.created_at)),
        ],
      }));

  const table = new DocxTable({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...dataRows],
  });

  return new DocxDocument({
    sections: [{
      properties: {},
      children: [
        new DocxParagraph({
          children: [new TextRun({ text: 'ADMIN · DIRECTORY', bold: true, color: '475569', size: 18 })],
        }),
        new DocxParagraph({
          children: [new TextRun({ text: 'Onboarded Doctors Report', bold: true, size: 36, color: '0F172A' })],
          heading: HeadingLevel.HEADING_1,
        }),
        new DocxParagraph({
          children: [
            new TextRun({ text: `Filter: `, bold: true, size: 18 }),
            new TextRun({ text: statusLabel, size: 18 }),
            new TextRun({ text: `    Count: `, bold: true, size: 18 }),
            new TextRun({ text: String(rows.length), size: 18 }),
          ],
        }),
        ...(search ? [new DocxParagraph({
          children: [
            new TextRun({ text: `Search: `, bold: true, size: 18 }),
            new TextRun({ text: `"${search}"`, size: 18 }),
          ],
        })] : []),
        new DocxParagraph({
          children: [
            new TextRun({ text: `Generated: `, bold: true, size: 18 }),
            new TextRun({ text: stamp, size: 18 }),
            ...(currentUser ? [
              new TextRun({ text: `    By: `, bold: true, size: 18 }),
              new TextRun({ text: currentUser, size: 18 }),
            ] : []),
          ],
        }),
        text(''),
        table,
      ],
    }],
  });
}

// ── Shared sx factory — rebuilt per-render from the active palette so the
// page repaints correctly when the user flips theme mode.
const buildSx = (T) => ({
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      bgcolor: T.fieldBg,
      color: T.text,
      '& fieldset': { borderColor: T.borderSubtle },
      '&:hover fieldset': { borderColor: T.borderStrong },
      '&.Mui-focused fieldset': { borderColor: T.primary },
    },
    '& .MuiInputBase-input::placeholder': { color: T.textDim, opacity: 1 },
  },
  select: {
    borderRadius: 1.5,
    bgcolor: T.fieldBg,
    color: T.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.borderSubtle },
    '& .MuiSvgIcon-root': { color: T.textMuted },
  },
  menuPaper: {
    bgcolor: T.surfaceAlt,
    color: T.text,
    border: `1px solid ${T.borderSubtle}`,
    '& .MuiMenuItem-root:hover': { bgcolor: T.rowHover },
  },
  headerCell: {
    bgcolor: T.surface,
    color: T.textMuted,
    borderBottom: `1px solid ${T.borderSubtle}`,
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    py: 1.5,
  },
  cell: {
    color: T.text,
    borderColor: T.borderSubtle,
    py: 1.5,
    fontSize: '0.86rem',
  },
  dialogField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      bgcolor: T.fieldBg,
      color: T.text,
      '& fieldset': { borderColor: T.borderSubtle },
      '&:hover fieldset': { borderColor: T.borderStrong },
      '&.Mui-focused fieldset': { borderColor: T.primary },
    },
    '& .MuiInputLabel-root': { color: T.textMuted },
    '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
    '& .MuiSelect-icon': { color: T.textMuted },
  },
});
